import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Switch,
  VStack,
  HStack,
  Grid,
  GridItem,
  useToast,
  Tag,
  TagLabel,
  TagCloseButton,
  Box,
  Text,
  Wrap,
  WrapItem,
  Icon,
  Divider
} from '@chakra-ui/react';
import { CalendarIcon, TimeIcon, EditIcon } from '@chakra-ui/icons';
import SearchableSelectChakra from './SearchableSelectChakra';
import { API_BASE_URL } from '../../../../utils/apiConfig';

const EventModal = ({ isOpen, onClose, event, clickedDateTime, onSave, onDelete }) => {
  const initialFormData = {
    title: '',
    description: '',
    type: 'appointment',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    allDay: false,
    location: '',
    visibility: 'private',
    color: '#3E5F56',
    participants: [],
    relatedCliente: '',
    reminders: [{ type: 'notification', minutesBefore: 15 }]
  };

  const [formData, setFormData] = useState(initialFormData);

  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState('');
  const toast = useToast();

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const canSetVisibility = userData.permissions?.some(p => 
    ['Master', 'Diretoria', 'Gestor'].includes(p)
  );

  useEffect(() => {
    if (event) {
      // Editing existing event
      const start = new Date(event.startDate);
      const end = new Date(event.endDate);
      
      setFormData({
        title: event.title || '',
        description: event.description || '',
        type: event.type || 'appointment',
        startDate: start.toISOString().split('T')[0],
        startTime: start.toTimeString().slice(0, 5),
        endDate: end.toISOString().split('T')[0],
        endTime: end.toTimeString().slice(0, 5),
        allDay: event.allDay || false,
        location: event.location || '',
        visibility: event.visibility || 'private',
        color: event.color || '#3E5F56',
        participants: event.participants?.map(p => p._id) || [],
        relatedCliente: event.relatedCliente?._id || '',
        reminders: event.reminders || [{ type: 'notification', minutesBefore: 15 }]
      });
    } else if (clickedDateTime) {
      // New event from calendar click
      const { date, time } = clickedDateTime;
      const dateStr = date.toISOString().split('T')[0];
      const startTime = time || '09:00';
      const endTime = time ? addMinutesToTime(time, 60) : '10:00';
      
      setFormData({
        ...initialFormData,
        startDate: dateStr,
        startTime: startTime,
        endDate: dateStr,
        endTime: endTime
      });
    } else {
      // New event from button
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      setFormData({
        ...initialFormData,
        startDate: dateStr,
        endDate: dateStr
      });
    }
  }, [event, clickedDateTime]);

  useEffect(() => {
    // Fetch users for participants and clients
    fetchUsers();
    fetchClients();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/clientes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClients(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const addMinutesToTime = (time, minutes) => {
    const [hours, mins] = time.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + minutes;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;
    return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddParticipant = () => {
    if (selectedParticipant && !formData.participants.includes(selectedParticipant)) {
      setFormData(prev => ({
        ...prev,
        participants: [...prev.participants, selectedParticipant]
      }));
      setSelectedParticipant('');
    }
  };

  const handleRemoveParticipant = (userId) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.filter(id => id !== userId)
    }));
  };

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      toast({
        title: 'Título obrigatório',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const startDateTime = new Date(`${formData.startDate}T${formData.startTime || '00:00'}`);
    const endDateTime = new Date(`${formData.endDate}T${formData.endTime || '23:59'}`);

    if (endDateTime <= startDateTime) {
      toast({
        title: 'Data/hora de término deve ser posterior ao início',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const eventData = {
      ...formData,
      startDate: startDateTime.toISOString(),
      endDate: endDateTime.toISOString()
    };

    onSave(eventData);
  };

  const handleDelete = () => {
    if (window.confirm('Tem certeza que deseja excluir este evento?')) {
      onDelete(event._id);
    }
  };

  const getParticipantName = (userId) => {
    const user = users.find(u => u._id === userId);
    return user ? user.nomeCompleto : userId;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="container.md" mx="auto">
        <ModalHeader>
          {event ? 'Editar Evento' : 'Novo Evento'}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Title and Description */}
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel display="flex" alignItems="center">
                  <Icon as={EditIcon} mr={2} color="green.500" />
                  Título
                </FormLabel>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Digite o título do evento"
                  size="lg"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Descrição</FormLabel>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Digite a descrição do evento"
                  rows={3}
                />
              </FormControl>
            </VStack>

            <Divider />

            {/* Type and Visibility */}
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
              <FormControl>
                <FormLabel>Tipo</FormLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  size="md"
                >
                  <option value="appointment">Compromisso</option>
                  <option value="meeting">Reunião</option>
                  <option value="task">Tarefa</option>
                  <option value="reminder">Lembrete</option>
                  <option value="other">Outro</option>
                </Select>
              </FormControl>

              {canSetVisibility && (
                <FormControl>
                  <FormLabel>Visibilidade</FormLabel>
                  <Select
                    name="visibility"
                    value={formData.visibility}
                    onChange={handleInputChange}
                    size="md"
                  >
                    <option value="private">Privado</option>
                    <option value="team">Equipe</option>
                    <option value="public">Público</option>
                  </Select>
                </FormControl>
              )}
            </Grid>

            <Divider />

            {/* Date and Time Section */}
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Text fontWeight="medium" fontSize="lg" display="flex" alignItems="center">
                  <Icon as={CalendarIcon} mr={2} color="green.500" />
                  Data e Hora
                </Text>
                <FormControl display="flex" alignItems="center" w="auto">
                  <FormLabel htmlFor="allDay" mb="0" mr={2}>
                    Dia inteiro
                  </FormLabel>
                  <Switch
                    id="allDay"
                    name="allDay"
                    isChecked={formData.allDay}
                    onChange={handleInputChange}
                    colorScheme="green"
                  />
                </FormControl>
              </HStack>

              <Grid templateColumns={{ base: '1fr', md: formData.allDay ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)' }} gap={4}>
                <GridItem colSpan={{ base: 1, md: 1 }}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Data de Início</FormLabel>
                    <Input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      size="md"
                    />
                  </FormControl>
                </GridItem>

                {!formData.allDay && (
                  <GridItem colSpan={{ base: 1, md: 1 }}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Hora de Início</FormLabel>
                      <Input
                        type="time"
                        name="startTime"
                        value={formData.startTime}
                        onChange={handleInputChange}
                        size="md"
                      />
                    </FormControl>
                  </GridItem>
                )}

                <GridItem colSpan={{ base: 1, md: 1 }}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Data de Término</FormLabel>
                    <Input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      size="md"
                    />
                  </FormControl>
                </GridItem>

                {!formData.allDay && (
                  <GridItem colSpan={{ base: 1, md: 1 }}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Hora de Término</FormLabel>
                      <Input
                        type="time"
                        name="endTime"
                        value={formData.endTime}
                        onChange={handleInputChange}
                        size="md"
                      />
                    </FormControl>
                  </GridItem>
                )}
              </Grid>
            </VStack>

            <Divider />

            <FormControl>
              <FormLabel>Local</FormLabel>
              <Input
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Digite o local do evento"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Cliente</FormLabel>
              <SearchableSelectChakra
                options={clients}
                value={formData.relatedCliente}
                onChange={(value) => setFormData(prev => ({ ...prev, relatedCliente: value }))}
                placeholder="Selecionar cliente..."
                displayField={(client) => `${client.razaoSocial || client.nomeFantasia || 'Cliente'} ${client.cnpj ? `(${client.cnpj})` : ''}`}
                valueField="_id"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Participantes</FormLabel>
              <Grid templateColumns="1fr auto" gap={2}>
                <SearchableSelectChakra
                  options={users.filter(user => !formData.participants.includes(user._id))}
                  value={selectedParticipant}
                  onChange={setSelectedParticipant}
                  placeholder="Buscar participante..."
                  displayField={(user) => `${user.nomeCompleto} (${user.email})`}
                  valueField="_id"
                />
                <Button 
                  onClick={handleAddParticipant} 
                  isDisabled={!selectedParticipant}
                  colorScheme="blue"
                  size="md"
                >
                  Adicionar
                </Button>
              </Grid>
              
              {formData.participants.length > 0 && (
                <Box mt={3}>
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    Participantes selecionados:
                  </Text>
                  <Wrap spacing={2}>
                    {formData.participants.map(userId => (
                      <WrapItem key={userId}>
                        <Tag size="lg" colorScheme="blue" borderRadius="full">
                          <TagLabel>{getParticipantName(userId)}</TagLabel>
                          <TagCloseButton onClick={() => handleRemoveParticipant(userId)} />
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Cor</FormLabel>
              <Input
                type="color"
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                w="100px"
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            {event && (
              <Button colorScheme="red" variant="outline" onClick={handleDelete}>
                Excluir
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              {event ? 'Salvar' : 'Criar'}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EventModal;